import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Badge,
	Box,
	Button,
	Flex,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Spinner,
	Text,
} from '@chakra-ui/react';
import { gql, useQuery } from '@apollo/client';

const QUERY_DOCUMENTS = gql`
	query getDigitizedDocuments($sourceType: String!, $sourceId: String!) {
		getDigitizedDocuments(sourceType: $sourceType, sourceId: $sourceId) {
			id
			label
			fileUrl
			signedFileUrl
			status
			error
			ocrEngine
			fileSize
			rawOcrText
			text
			createdAt
		}
	}
`;

const STATUS_COLOR = {
	done: 'green',
	failed: 'red',
	skipped: 'gray',
};

function DocumentRow({ document }) {
	const { t } = useTranslation();
	const hasBoth = !!(document.rawOcrText && document.text);
	const [showRaw, setShowRaw] = useState(false);
	// Cleaned text once it exists; otherwise fall back to the raw OCR read
	// rather than showing nothing while a document is awaiting cleanup.
	const shownText = showRaw ? document.rawOcrText : document.text || document.rawOcrText;
	const awaitingCleanup = !!document.rawOcrText && !document.text;

	return (
		<AccordionItem>
			<h2>
				<AccordionButton>
					<Flex flex="1" align="center" gap="8px" textAlign="left">
						<Badge colorScheme={STATUS_COLOR[document.status] || 'gray'}>{t(document.status)}</Badge>
						{awaitingCleanup && <Badge colorScheme="orange">{t('awaiting cleanup')}</Badge>}
						<Text fontWeight="600" fontSize="13px">
							{document.label || t('File')}
						</Text>
						<Text fontSize="11px" color="gray.500">
							({document.ocrEngine})
						</Text>
					</Flex>
					<AccordionIcon />
				</AccordionButton>
			</h2>
			<AccordionPanel pb={4}>
				<Flex gap="8px" mb="3" wrap="wrap">
					{document.signedFileUrl && (
						<Button as="a" href={document.signedFileUrl} target="_blank" rel="noreferrer" size="xs" colorScheme="blue">
							{t('View file')}
						</Button>
					)}
					{hasBoth && (
						<Button size="xs" onClick={() => setShowRaw((prev) => !prev)}>
							{showRaw ? t('Show cleaned text') : t('Show raw OCR text')}
						</Button>
					)}
				</Flex>

				{document.error && (
					<Text fontSize="12px" color="red.600" mb="2">
						{document.error}
					</Text>
				)}

				{awaitingCleanup && (
					<Text fontSize="12px" color="orange.600" mb="2">
						{t('OCR done — AI cleanup not run yet (showing raw OCR text)')}
					</Text>
				)}

				{shownText ? (
					<Box
						as="pre"
						whiteSpace="pre-wrap"
						fontSize="12px"
						bg="gray.50"
						border="1px solid #e2e2e2"
						borderRadius="6px"
						p="12px"
						maxH="320px"
						overflowY="auto"
					>
						{shownText}
					</Box>
				) : (
					<Text fontSize="12px" color="gray.500">
						{t('No extracted text')}
					</Text>
				)}
			</AccordionPanel>
		</AccordionItem>
	);
}

function DigitizerDetailModal({ isOpen, onClose, sourceType, sourceId }) {
	const { t } = useTranslation();
	const { data, loading, error } = useQuery(QUERY_DOCUMENTS, {
		skip: !isOpen || !sourceType || !sourceId,
		variables: { sourceType, sourceId },
		fetchPolicy: 'network-only',
	});

	const documents = data?.getDigitizedDocuments || [];

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
			<ModalOverlay />
			<ModalContent>
				<ModalHeader fontSize="md">{t('Digitized files')}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					{loading && (
						<Flex justify="center" py="24px">
							<Spinner />
						</Flex>
					)}
					{!loading && error && (
						<Text fontSize="13px" color="red.600">
							{t('Could not load files')}
						</Text>
					)}
					{!loading && !error && documents.length === 0 && (
						<Text fontSize="13px" color="gray.500">
							{t('No files digitized for this record')}
						</Text>
					)}
					{!loading && !error && documents.length > 0 && (
						<Accordion allowToggle defaultIndex={[0]}>
							{documents.map((document) => (
								<DocumentRow key={document.id} document={document} />
							))}
						</Accordion>
					)}
				</ModalBody>
				<ModalFooter>
					<Button size="sm" onClick={onClose}>
						{t('Close')}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}

export default DigitizerDetailModal;
